<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import { ChevronRight } from 'lucide-vue-next'

import MarketingFlowActionSummary from '@/views/autopilot/Components/MarketingFlowActionSummary.vue'

import type { MarketingFlowExecutionActionStatus, MarketingFlowExecutionStatus } from '../types/marketingFlow'
import type { MarketingFlowExecutionCustomerStat } from '@/views/autopilot/Components/MarketingFlowPerformance.vue'

const props = defineProps<{
  marketingFlowId: number
  stats: MarketingFlowExecutionCustomerStat[]
  currentPage: number
  lastPage: number
  total: number
  pageSize: number
  isFetching: boolean
}>()

defineEmits(['change-page', 'search'])

const { t } = useI18n()

const columns = {
  contact: {
    label: t('pages.marketingFlow.marketingFlowPerformance.columns.contact'),
    headerClass: 'w-1/4'
  },
  triggerCount: {
    label: t('pages.marketingFlow.marketingFlowPerformance.columns.triggerCount'),
    headerClass: 'w-1/4',
    numeric: true
  },
  firstTriggeredAt: {
    label: t('pages.marketingFlow.marketingFlowPerformance.columns.firstTriggeredAt'),
    headerClass: 'w-1/4'
  },
  lastTriggeredAt: {
    label: t('pages.marketingFlow.marketingFlowPerformance.columns.lastTriggeredAt'),
    headerClass: 'w-1/4'
  }
}

const rows = computed(() => {
  return props.stats?.map((stat) => {
    const { phone, firstName, lastName, gender, id, hasOptin } = stat.customer_merchant
    const name = [firstName, lastName].filter(Boolean).join(' ')

    return {
      id: stat.customer_merchant.id,
      data: {
        contact: {
          id,
          hasOptin,
          gender: gender || '',
          phone,
          name: name || '-'
        },
        triggerCount: stat.triggered_count,
        firstTriggeredAt: stat.first_triggered_at,
        lastTriggeredAt: stat.last_triggered_at
      }
    }
  })
})
const pagination = computed(() => ({
  currentPage: props.currentPage,
  total: props.total,
  lastPage: props.lastPage,
  pageSize: props.pageSize
}))

const executionsDetails = ref<{ [customerId: number]: any[] }>({})
const executionsDetailsPaginations = ref<{
  [customerId: number]: { currentPage: number; lastPage: number; total: number }
}>({})
const expandedRows = ref<number[]>([])

const handleRowClick = (event: any) => {
  toggleExecutionsDetails(event.id)
}

const toggleExecutionsDetails = async (customerId: number) => {
  const isExpanded = expandedRows.value.includes(customerId)

  if (isExpanded) {
    expandedRows.value = expandedRows.value.filter((id) => id !== customerId)
    executionsDetails.value[customerId] = undefined
    return
  }

  expandedRows.value.push(customerId)
  fetchExecutionsDetails(customerId)
}

const fetchExecutionsDetails = async (customerId: number, page: number = 1) => {
  const { data } = await axios.get(`/marketing-flows/${props.marketingFlowId}/executions/customers/${customerId}`, {
    params: {
      page
    }
  })

  executionsDetails.value[customerId] = data.data.map((execution: any) => ({
    key: execution.id,
    label: execution.triggered_at,
    data: execution,
    type: 'execution',
    children: execution.actions.map((action: any) => ({
      key: action.id,
      label: action.id,
      data: action,
      type: 'action'
    }))
  }))

  executionsDetailsPaginations.value[customerId] = {
    currentPage: data.meta.current_page,
    lastPage: data.meta.last_page,
    total: data.meta.total
  }
}

const getStatusColor = (status: MarketingFlowExecutionStatus | MarketingFlowExecutionActionStatus) => {
  switch (status) {
    case 'COMPLETED':
      return 'green'
    case 'FAILED':
      return 'red'
    case 'IN_PROGRESS':
    case 'WAITING_FOR_CLICK':
      return 'yellow'
    case 'PENDING':
    case 'NOT_STARTED':
      return 'gray'
    case 'CANCELLED':
      return 'gray'
  }
}

const getStatusLabel = (status: MarketingFlowExecutionStatus | MarketingFlowExecutionActionStatus) => {
  switch (status) {
    case 'COMPLETED':
      return t('pages.marketingFlow.marketingFlowPerformance.status.completed')
    case 'FAILED':
      return t('pages.marketingFlow.marketingFlowPerformance.status.failed')
    case 'IN_PROGRESS':
      return t('pages.marketingFlow.marketingFlowPerformance.status.inProgress')
    case 'WAITING_FOR_CLICK':
      return t('pages.marketingFlow.marketingFlowPerformance.status.waitingForClick')
    case 'NOT_STARTED':
    case 'PENDING':
      return t('pages.marketingFlow.marketingFlowPerformance.status.notStarted')
    case 'CANCELLED':
      return t('pages.marketingFlow.marketingFlowPerformance.status.cancelled')
  }
}
</script>

<template>
  <div>
    <AppTable
      :columns="columns"
      :rows="rows"
      :pagination="pagination"
      :expanded-rows="expandedRows"
      :is-loading="isFetching"
      row-classes="cursor-pointer"
      @row-clicked="handleRowClick"
      @update:change-page="$emit('change-page', $event)"
    >
      <template #title>
        <div class="flex items-center justify-between">
          <CardTitle>{{ t('pages.marketingFlow.marketingFlowPerformance.table.contactTitle') }}</CardTitle>
          <div class="relative">
            <Input
              name="search"
              type="search"
              class="pl-10 sm:w-96"
              :placeholder="t('pages.marketingFlow.marketingFlowPerformance.table.searchPlaceholder')"
              @input="$emit('search', ($event.target as HTMLInputElement).value)"
            />
            <BaseIcon
              name="HeroMagnifyingGlassIconOutline"
              class="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-foreground"
            />
          </div>
        </div>
      </template>

      <template #td-contact="{ data }">
        <RowCustomer
          :id="data.contact.id"
          :has-optin="data.contact.hasOptin"
          :phone="data.contact.phone"
          :name="data.contact.name"
        />
      </template>

      <template #td-firstTriggeredAt="{ data }">
        <p class="text-sm font-medium text-foreground">
          {{ dayjs(data.firstTriggeredAt).format('DD/MM/YYYY HH:mm:ss') }}
        </p>
      </template>

      <template #td-lastTriggeredAt="{ data }">
        <p class="text-sm font-medium text-foreground">
          {{ dayjs(data.lastTriggeredAt).format('DD/MM/YYYY HH:mm:ss') }}
        </p>
      </template>

      <template #expanded-content="{ row }">
        <div class="text-sm py-1">
          <Collapsible
            v-for="execution in executionsDetails[row.id]"
            :key="execution.key"
            :default-open="false"
            class="border-b border-border last:border-0"
          >
            <CollapsibleTrigger class="group flex w-full items-center gap-2 px-4 py-2 hover:bg-muted/50 text-left">
              <ChevronRight
                class="size-4 shrink-0 transition-transform duration-150 group-data-[state=open]:rotate-90 text-muted-foreground"
              />
              <BaseChip
                size="sm"
                :text="getStatusLabel(execution.data.status)"
                :color="getStatusColor(execution.data.status)"
              />
              <span class="text-foreground">
                {{
                  t('pages.marketingFlow.marketingFlowPerformance.execution.triggeredAt', {
                    date: new Date(execution.data.triggered_at).toLocaleDateString(),
                    time: new Date(execution.data.triggered_at).toLocaleTimeString()
                  })
                }}
              </span>
              <span class="text-muted-foreground">-</span>
              <span class="text-muted-foreground">
                {{
                  t('pages.marketingFlow.marketingFlowPerformance.actions.count', {
                    count: execution.data.actions.length
                  })
                }}
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div
                v-for="action in execution.children"
                :key="action.key"
                class="pl-10 pr-4 py-2 border-t border-border hover:bg-muted/50"
              >
                <MarketingFlowActionSummary
                  :action="action.data"
                  :get-status-label="getStatusLabel(action.data.status)"
                  :get-status-color="getStatusColor(action.data.status)"
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </template>
    </AppTable>
  </div>
</template>
